/**
 * Phase 6 — Communication Center API helpers (additive).
 * All endpoints live under /api/platform/comms/*.
 */
import { apiClient } from "./api";

/* ── Conversations & Messages ─────────────────────────────────── */
export const commsAPI = {
  listConversations: (params = {}) =>
    apiClient.get("/platform/comms/conversations", { params }).then((r) => r.data),

  startConversation: (payload) =>
    apiClient.post("/platform/comms/conversations", payload).then((r) => r.data),

  getConversation: (id) =>
    apiClient.get(`/platform/comms/conversations/${id}`).then((r) => r.data),

  getMessages: (id, params = {}) =>
    apiClient.get(`/platform/comms/conversations/${id}/messages`, { params }).then((r) => r.data),

  sendMessage: (payload) =>
    apiClient.post("/platform/comms/messages", payload).then((r) => r.data),

  markRead: (id) =>
    apiClient.post(`/platform/comms/conversations/${id}/read`).then((r) => r.data),

  /* ── Bookings ─ */
  createBooking: (payload) =>
    apiClient.post("/platform/comms/bookings", payload).then((r) => r.data),

  myBookings: (params = {}) =>
    apiClient.get("/platform/comms/bookings/mine", { params }).then((r) => r.data),

  businessBookings: (businessId, params = {}) =>
    apiClient.get(`/platform/comms/business/${businessId}/bookings`, { params }).then((r) => r.data),

  bookingDecision: (businessId, bookingId, action, body = {}) =>
    apiClient
      .post(`/platform/comms/business/${businessId}/bookings/${bookingId}/${action}`, body)
      .then((r) => r.data),

  cancelMyBooking: (bookingId, reason) =>
    apiClient.post(`/platform/comms/bookings/${bookingId}/cancel`, { reason }).then((r) => r.data),

  /* ── Customer Requests ─ */
  createRequest: (payload) =>
    apiClient.post("/platform/comms/requests", payload).then((r) => r.data),

  myRequests: (params = {}) =>
    apiClient.get("/platform/comms/requests/mine", { params }).then((r) => r.data),

  businessRequests: (businessId, params = {}) =>
    apiClient.get(`/platform/comms/business/${businessId}/requests`, { params }).then((r) => r.data),

  updateRequestStatus: (businessId, requestId, body) =>
    apiClient
      .post(`/platform/comms/business/${businessId}/requests/${requestId}/status`, body)
      .then((r) => r.data),

  addInternalNote: (businessId, requestId, text) =>
    apiClient
      .post(`/platform/comms/business/${businessId}/requests/${requestId}/notes`, { text })
      .then((r) => r.data),

  /* ── Notifications ─ */
  notifications: (params = {}) =>
    apiClient.get("/platform/comms/notifications", { params }).then((r) => r.data),

  markNotifRead: (id) =>
    apiClient.post(`/platform/comms/notifications/${id}/read`).then((r) => r.data),

  markAllNotifsRead: () =>
    apiClient.post("/platform/comms/notifications/read-all").then((r) => r.data),
};

/* ── Enums (mirror backend) ───────────────────────────────────── */
export const BOOKING_KINDS = [
  { key: "vehicle_inspection", ar: "فحص مركبة", en: "Vehicle Inspection" },
  { key: "periodic_inspection", ar: "فحص دوري", en: "Periodic Inspection" },
  { key: "workshop", ar: "ورشة صيانة", en: "Workshop" },
  { key: "test_drive", ar: "تجربة قيادة", en: "Test Drive" },
  { key: "car_rental", ar: "تأجير سيارة", en: "Car Rental" },
  { key: "consultation", ar: "استشارة", en: "Consultation" },
];

export const REQUEST_KINDS = [
  { key: "purchase", ar: "طلب شراء", en: "Purchase" },
  { key: "finance", ar: "طلب تمويل", en: "Finance" },
  { key: "insurance", ar: "طلب تأمين", en: "Insurance" },
  { key: "rental", ar: "طلب تأجير", en: "Rental" },
  { key: "inspection", ar: "طلب فحص", en: "Inspection" },
  { key: "service", ar: "طلب خدمة", en: "Service" },
  { key: "other", ar: "أخرى", en: "Other" },
  /* ── Phase 2D extended kinds ─────────────────────────────── */
  { key: "finance_inquiry",           ar: "طلب تمويل",           en: "Finance Inquiry" },
  { key: "insurance_quote",           ar: "طلب عرض تأمين",       en: "Insurance Quote" },
  { key: "bnpl_application",          ar: "طلب تقسيط",           en: "BNPL Application" },
  { key: "leasing_inquiry",           ar: "طلب تأجير تمويلي",    en: "Leasing Inquiry" },
  { key: "rental_reservation",        ar: "طلب حجز إيجار",       en: "Rental Reservation" },
  { key: "payment_solution_inquiry",  ar: "طلب حل دفع",          en: "Payment Solution Inquiry" },
  { key: "part_inquiry",              ar: "استفسار قطعة غيار",    en: "Part Inquiry" },
  { key: "recovery_request",          ar: "طلب سطحة",            en: "Recovery Request" },
  { key: "transport_quote",           ar: "طلب عرض نقل",         en: "Transport Quote" },
  { key: "detailing_booking",         ar: "حجز غسيل/تلميع",      en: "Detailing Booking" },
];

export const BOOKING_STATUS_LABELS = {
  pending:     { ar: "قيد المراجعة",  en: "Pending",     tone: "amber" },
  accepted:    { ar: "مقبول",         en: "Accepted",    tone: "green" },
  rescheduled: { ar: "معاد جدولته",   en: "Rescheduled", tone: "blue"  },
  rejected:    { ar: "مرفوض",         en: "Rejected",    tone: "red"   },
  canceled:    { ar: "ملغى",          en: "Canceled",    tone: "gray"  },
  completed:   { ar: "منجز",          en: "Completed",   tone: "green" },
  no_show:     { ar: "لم يحضر",       en: "No-show",     tone: "gray"  },
};

export const REQUEST_STATUS_LABELS = {
  open:        { ar: "مفتوح",       en: "Open",        tone: "amber" },
  in_progress: { ar: "قيد الدراسة",  en: "In Progress", tone: "blue"  },
  won:         { ar: "تمت الموافقة", en: "Won",         tone: "green" },
  lost:        { ar: "لم يُقبل",     en: "Lost",         tone: "red"   },
  closed:      { ar: "مغلق",         en: "Closed",      tone: "gray"  },
};

export const CONV_KINDS = [
  { key: "buyer_seller",       ar: "بائع/مشتري",   en: "Buyer ↔ Seller" },
  { key: "customer_dealer",    ar: "معرض",          en: "Dealer"          },
  { key: "customer_agency",    ar: "وكالة",         en: "Agency"          },
  { key: "customer_insurance", ar: "شركة تأمين",    en: "Insurance"       },
  { key: "customer_finance",   ar: "شركة تمويل",    en: "Finance"         },
  { key: "customer_rental",    ar: "شركة تأجير",    en: "Rental"          },
  { key: "customer_workshop",  ar: "ورشة",          en: "Workshop"        },
  { key: "customer_inspection",ar: "مركز فحص",     en: "Inspection"      },
];
